import React from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';

/**
 * Error boundary around the lazy-loaded route tree in App.js.
 *
 * Without it any render/effect error, or a failed lazy chunk import,
 * unmounts the whole React root and the user sees a blank white page.
 *
 * Chunk-load failures are the common case after a deploy: `deploy.sh`
 * wipes /static, so a tab (or installed PWA) that still holds the old
 * index.html requests chunk hashes that no longer exist. For those we
 * reload once (guarded per-path in sessionStorage so a genuinely broken
 * build cannot loop) — the fresh index.html then references live chunks.
 * Everything else gets a visible message with a reload button.
 */
const RELOAD_KEY = 'ft-chunk-reload';

const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = String(error.message || '');
  return (
    /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) || // Safari wording
    /error loading dynamically imported module/i.test(msg) // Firefox wording
  );
};

class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RouteErrorBoundary caught:', error, errorInfo);

    if (!isChunkLoadError(error)) return;

    const path = this.props.pathname || '';
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === path;
      if (!alreadyReloaded) sessionStorage.setItem(RELOAD_KEY, path);
    } catch {
      // storage unavailable (private mode etc.) — fall through to a single reload attempt
    }
    if (!alreadyReloaded) {
      window.location.reload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const chunk = isChunkLoadError(this.state.error);
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h5">
          {chunk ? 'Ny versjon tilgjengelig' : 'Noe gikk galt'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          {chunk
            ? 'Siden du hadde åpen er utdatert. Last siden på nytt for å hente den nyeste versjonen.'
            : 'Det oppstod en uventet feil på denne siden. Prøv å laste siden på nytt.'}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Last siden på nytt
        </Button>
      </Box>
    );
  }
}

// Keyed on pathname so navigating to another route clears a caught error.
const RouteErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundaryInner key={pathname} pathname={pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
};

export default RouteErrorBoundary;
